import { Button, Popover, PopoverContent, PopoverTrigger } from '@menage/ui';
import { useQuery } from '@tanstack/react-query';
import { UserCog } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { get } from '@/lib/api';
import { homePathFor, useAuth } from '@/lib/auth/AuthProvider';

interface DevAccount {
  email: string;
  fullName: string;
  role: string;
}

/**
 * DEV ROLE SWITCHER: one click to test the admin, supervisor, employee and client
 * experiences without Supabase. Rendered only by the Vite dev server, and the API
 * refuses dev logins unless DEV_ROLE_SWITCHER=true outside production.
 */
export function DevRoleSwitcher() {
  const { t } = useTranslation();
  const { actor, devLogin } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const enabled = import.meta.env.DEV;
  const { data: config } = useQuery({ queryKey: ['auth-config'], queryFn: () => get<{ devRoleSwitcher: boolean }>('/auth/config'), enabled, staleTime: Infinity });
  const { data: accounts } = useQuery({ queryKey: ['dev-accounts'], queryFn: () => get<DevAccount[]>('/auth/dev-accounts'), enabled: enabled && !!config?.devRoleSwitcher, staleTime: Infinity });
  if (!enabled || !config?.devRoleSwitcher || !accounts?.length) return null;

  return (
    <div className="fixed bottom-20 right-3 z-40 lg:bottom-4">
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="border-dashed border-tone-amber bg-card/95 text-tone-amber shadow-lg" aria-label={t('labels.devSwitcher')}>
            <UserCog /> DEV
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2">
          <p className="px-2 pb-1 pt-1 text-sm font-semibold">{t('labels.devSwitcher')}</p>
          <p className="px-2 pb-2 text-xs text-muted-foreground">{t('labels.devSwitcherHint')}</p>
          <div className="space-y-0.5">
            {accounts.map((a) => (
              <button
                key={a.email}
                disabled={busy !== null}
                onClick={async () => {
                  setBusy(a.email);
                  try {
                    const next = await devLogin(a.email);
                    navigate(homePathFor(next));
                  } finally {
                    setBusy(null);
                  }
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-muted ${actor?.email === a.email ? 'bg-secondary' : ''}`}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{a.fullName}</span>
                  <span className="block truncate text-xs text-muted-foreground">{a.email}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{busy === a.email ? '…' : t(`roles.${a.role}`)}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
